import { useState } from "react";
import { useSeason } from "@/lib/SeasonContext";
import { api, formatError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, ChevronDown, History, Trash2, Settings2 } from "lucide-react";
import { toast } from "sonner";

export default function SeasonSwitcher({ canEdit = false }) {
  const { activeSeason, seasons, changeSeason, startNewSeason, refresh } = useSeason();
  const [newSeasonOpen, setNewSeasonOpen] = useState(false);
  const [newSeason, setNewSeason] = useState("");
  const [archiveSeniors, setArchiveSeniors] = useState(true);
  const [busy, setBusy] = useState(false);

  const [pastSeasonOpen, setPastSeasonOpen] = useState(false);
  const [pastSeason, setPastSeason] = useState("");
  const [pastBusy, setPastBusy] = useState(false);

  const [manageOpen, setManageOpen] = useState(false);
  const [deletingSeason, setDeletingSeason] = useState(null);

  if (!activeSeason) return null;

  const handleChange = async (val) => {
    if (val === "__new__") { setNewSeasonOpen(true); return; }
    if (val === "__past__") { setPastSeasonOpen(true); return; }
    if (val === "__manage__") { setManageOpen(true); return; }
    try {
      await changeSeason(val);
      toast.success(`Active season: ${val}`);
    } catch (e) { toast.error("Couldn't switch season"); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!/^\d{4}-\d{2}$/.test(newSeason)) {
      toast.error("Season format must be like 2026-27");
      return;
    }
    setBusy(true);
    try {
      const data = await startNewSeason(newSeason, archiveSeniors);
      toast.success(`Season ${data.active_season} started · ${data.archived_seniors} seniors archived`);
      setNewSeasonOpen(false);
      setNewSeason("");
    } catch { toast.error("Couldn't start new season"); }
    finally { setBusy(false); }
  };

  const handleAddPast = async (e) => {
    e.preventDefault();
    if (!/^\d{4}-\d{2}$/.test(pastSeason)) {
      toast.error("Season format must be like 2022-23");
      return;
    }
    setPastBusy(true);
    try {
      const { data } = await api.post("/seasons", { season: pastSeason });
      toast.success(`Season ${data.season} added — switch to it below to add historical data`);
      await refresh();
      setPastSeasonOpen(false);
      setPastSeason("");
      // Auto-switch into the newly added past season so the coach can start entering data right away
      await changeSeason(data.season);
      toast.info(`Now viewing ${data.season}`);
    } catch (err) { toast.error(formatError(err)); }
    finally { setPastBusy(false); }
  };

  const handleDeleteSeason = async (season) => {
    if (season === activeSeason) { toast.error("Switch to a different season first"); return; }
    const msg = `Permanently delete season ${season}?\n\nThis removes ALL games, per-game stats, practice sessions, attendance, and any players tagged only to that season. This can't be undone.`;
    if (!confirm(msg)) return;
    setDeletingSeason(season);
    try {
      const { data } = await api.delete(`/seasons/${encodeURIComponent(season)}`);
      const parts = [
        data.games_deleted > 0 && `${data.games_deleted} games`,
        data.stats_deleted > 0 && `${data.stats_deleted} stat rows`,
        data.sessions_deleted > 0 && `${data.sessions_deleted} practice sessions`,
        data.attendance_deleted > 0 && `${data.attendance_deleted} attendance records`,
        data.players_deleted > 0 && `${data.players_deleted} players`,
      ].filter(Boolean).join(", ");
      toast.success(`Season ${season} deleted${parts ? ` · removed ${parts}` : ""}`);
      if (data.orphaned_players_kept > 0) {
        toast.info(`Kept ${data.orphaned_players_kept} player${data.orphaned_players_kept === 1 ? "" : "s"} with stats in other seasons`);
      }
      await refresh();
    } catch (err) { toast.error(formatError(err)); }
    finally { setDeletingSeason(null); }
  };

  return (
    <>
      <div className="px-3 pb-2">
        <p className="eyebrow text-[9px] mb-1.5 px-1">Active Season</p>
        {canEdit ? (
          <Select value={activeSeason} onValueChange={handleChange}>
            <SelectTrigger
              className="w-full bg-ink-800 border-ink-700 text-white text-sm font-display font-bold uppercase tracking-wider h-9"
              data-testid="season-switcher"
            >
              <SelectValue>{activeSeason}</SelectValue>
            </SelectTrigger>
            <SelectContent className="bg-ink-900 border-ink-700">
              {seasons.map((s) => (
                <SelectItem key={s} value={s} className="text-white">{s}</SelectItem>
              ))}
              <div className="my-1 border-t border-ink-700/60" />
              <SelectItem value="__new__" className="text-amber-300 focus:text-amber-200 focus:bg-burgundy/20 font-semibold">
                + Start New Season…
              </SelectItem>
              <SelectItem value="__past__" className="text-amber-300 focus:text-amber-200 focus:bg-burgundy/20 font-semibold">
                + Add Past Season…
              </SelectItem>
              <SelectItem value="__manage__" className="text-ink-300 focus:text-white focus:bg-ink-800">
                <Settings2 className="w-3 h-3 inline mr-1" /> Manage Seasons…
              </SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <div className="w-full bg-ink-800 border border-ink-700 rounded-md px-3 py-1.5 text-white text-sm font-display font-bold uppercase tracking-wider flex items-center justify-between">
            <span>{activeSeason}</span>
            {seasons.length > 1 && <ChevronDown className="w-3 h-3 text-ink-400" />}
          </div>
        )}
      </div>

      <Dialog open={newSeasonOpen} onOpenChange={(v) => !v && setNewSeasonOpen(false)}>
        <DialogContent className="bg-ink-900 border-ink-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="heading-3 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-burgundy" /> Start New Season
            </DialogTitle>
            <DialogDescription className="text-ink-300">
              All future games will be tagged with this new season label. Past seasons remain searchable.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4" data-testid="new-season-form">
            <p className="text-sm text-ink-300">
              This sets a new active season. All future games and stats you enter will be tagged under it.
              Past seasons remain fully searchable from the dropdown.
            </p>
            <div>
              <Label className="eyebrow">Season label</Label>
              <Input
                value={newSeason}
                onChange={(e) => setNewSeason(e.target.value)}
                placeholder="2026-27"
                className="bg-ink-800 border-ink-700 mt-2"
                data-testid="new-season-input"
                required
              />
              <p className="text-xs text-ink-400 mt-1">Format: YYYY-YY (e.g. 2026-27)</p>
            </div>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={archiveSeniors}
                onChange={(e) => setArchiveSeniors(e.target.checked)}
                className="mt-0.5"
                data-testid="archive-seniors-checkbox"
              />
              <span>
                <span className="text-white">Archive 12th-grade seniors</span>
                <span className="block text-xs text-ink-400">Moves graduating seniors to the alumni archive. Their stats are preserved.</span>
              </span>
            </label>
            <Button type="submit" disabled={busy} className="w-full bg-burgundy hover:bg-burgundy-hover" data-testid="new-season-submit">
              {busy ? "Starting…" : "Start Season"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add PAST season dialog */}
      <Dialog open={pastSeasonOpen} onOpenChange={(v) => !v && setPastSeasonOpen(false)}>
        <DialogContent className="bg-ink-900 border-ink-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="heading-3 flex items-center gap-2">
              <History className="w-5 h-5 text-burgundy" /> Add Past Season
            </DialogTitle>
            <DialogDescription className="text-ink-300">
              Register a historical season so you can enter games, alumni players, and old stats. Nothing changes for your current season.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddPast} className="space-y-4" data-testid="past-season-form">
            <div>
              <Label className="eyebrow">Quick pick</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {["2024-25", "2023-24", "2022-23"].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setPastSeason(s)}
                    className={`px-3 py-1.5 rounded text-xs uppercase tracking-wider font-bold border transition ${
                      pastSeason === s
                        ? "bg-burgundy border-burgundy text-white"
                        : "border-ink-700 text-ink-300 hover:bg-ink-800 hover:text-white"
                    }`}
                    data-testid={`past-season-preset-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="eyebrow">Or type any season</Label>
              <Input
                value={pastSeason}
                onChange={(e) => setPastSeason(e.target.value)}
                placeholder="2022-23"
                className="bg-ink-800 border-ink-700 mt-2"
                data-testid="past-season-input"
                required
              />
              <p className="text-xs text-ink-400 mt-1">Format: YYYY-YY. After adding, we&apos;ll switch you into that season so you can start entering games and players.</p>
            </div>
            <Button type="submit" disabled={pastBusy} className="w-full bg-burgundy hover:bg-burgundy-hover" data-testid="past-season-submit">
              {pastBusy ? "Adding…" : "Add & Switch Into Season"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Manage / delete seasons */}
      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="bg-ink-900 border-ink-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="heading-3 flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-burgundy" /> Manage Seasons
            </DialogTitle>
            <DialogDescription className="text-ink-300">
              Delete a season and everything attached to it. The active season <span className="text-white font-semibold">{activeSeason}</span> cannot be deleted — switch away first.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2" data-testid="manage-seasons-list">
            {seasons.filter((s) => s !== activeSeason).length === 0 ? (
              <p className="text-sm text-ink-400 text-center py-4">No other seasons to manage.</p>
            ) : (
              seasons.filter((s) => s !== activeSeason).map((s) => (
                <div key={s} className="flex items-center justify-between bg-ink-800 border border-ink-700 rounded-md px-3 py-2" data-testid={`manage-season-row-${s}`}>
                  <span className="font-display font-bold uppercase tracking-wider text-white">{s}</span>
                  <button
                    onClick={() => handleDeleteSeason(s)}
                    disabled={deletingSeason === s}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10 border border-red-500/30 hover:border-red-500/60 rounded px-2 py-1 text-[10px] uppercase tracking-wider font-bold transition disabled:opacity-50 inline-flex items-center gap-1.5"
                    data-testid={`delete-season-${s}`}
                  >
                    <Trash2 className="w-3 h-3" />
                    {deletingSeason === s ? "Deleting…" : "Delete"}
                  </button>
                </div>
              ))
            )}
          </div>
          <p className="text-[11px] text-ink-400 mt-2">Deleting a season removes its games, per-game stats, practice sessions, attendance records, and any players tagged only to that season. Players with stats in other seasons are preserved.</p>
        </DialogContent>
      </Dialog>
    </>
  );
}
